import { FC, useMemo } from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { Replay } from "@mui/icons-material";
import { useCompanyPageStore } from "@/store/companyPageStore";
import { useIntentFiltersStorage } from "./hooks/useIntentFiltersStorage";
import { IntentTopicsMenu } from "./IntentTopicsMenu";
import {
  TopicFilter,
  BuyersJourneyFilter,
  ContextTypeFilter,
  LocationFilter,
} from "./Filters";

/**
 * Filter bar for Intent Activity table
 * Includes placeholder filter chips and corporate descendants toggle
 */
export const IntentFilters: FC = () => {
  const company = useCompanyPageStore((state) => state.company);
  const { intentFilters, setIntentFilters, resetIntentFilters } =
    useIntentFiltersStorage();

  const handleToggleCorporateDescendants = () => {
    setIntentFilters((prev) => ({
      ...prev,
      includeCorporateDescendants: !prev.includeCorporateDescendants,
    }));
  };

  // Check if any filters are applied
  const hasFiltersApplied = useMemo(() => {
    return (
      intentFilters.topicIds.length > 0 ||
      intentFilters.buyersJourneyIds.length > 0 ||
      intentFilters.contextTypeIds.length > 0 ||
      intentFilters.locationCountryAlpha2s.length > 0 ||
      intentFilters.locationStateIds.length > 0 ||
      intentFilters.locationRegionNames.length > 0 ||
      intentFilters.includeCorporateDescendants ||
      intentFilters.techTopicsOnly
    );
  }, [intentFilters]);

  // Corporate toggle shows only when company is org root with descendants
  const shouldShowCorporateToggle = useMemo(() => {
    return (
      company &&
      company.hgId === company.orgTree?.root &&
      (company.orgTree?.descendants.length ?? 0) > 1
    );
  }, [company]);

  return (
    <Stack
      direction="row"
      spacing={1}
      flexWrap="wrap"
      alignItems="center"
      justifyContent="space-between"
    >
      {/* Filter Chips - Left Side */}
      <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
        <TopicFilter />
        <BuyersJourneyFilter />
        <ContextTypeFilter />
        <LocationFilter />

        {/* Intent Topics Menu (tech topics toggle) */}
        <IntentTopicsMenu />

        {/* Reset Filters Button */}
        {hasFiltersApplied && (
          <Tooltip title="Reset Filters">
            <IconButton
              size="small"
              sx={{ height: 24, width: 24 }}
              onClick={resetIntentFilters}
            >
              <Replay sx={{ height: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {/* Corporate Descendants Toggle - Right Side */}
      {shouldShowCorporateToggle && (
        <FormControlLabel
          control={
            <Switch
              checked={intentFilters.includeCorporateDescendants || false}
              onChange={handleToggleCorporateDescendants}
              size="small"
            />
          }
          label={
            <Typography variant="body2">
              Show intent across the entire organization
            </Typography>
          }
        />
      )}
    </Stack>
  );
};
